import { formatTime24 } from '../../chat/components/ChatDetailsPanel/utils/followUpUtils';

export const FOLLOW_UP_TABS = [
  { key: 'due', label: 'Due' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'trash', label: 'Trash' },
];

const startOfDay = (date, offsetDays = 0) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return d;
};

const dayDiff = (iso, now) => Math.round((startOfDay(iso) - startOfDay(now)) / (24 * 60 * 60 * 1000));

export const isOverdue = (item, now = new Date()) =>
  item.status === 'pending' && !item.deleted_at && !!item.due_at && new Date(item.due_at) <= now;

/** Cancelled = status 2 / status_label "Cancelled" (normalized to status 'cancelled'). */
export const isFollowUpCancelled = (item) => item?.status === 'cancelled';

/**
 * Trash = trashed or cancelled; Completed; Due = pending whose scheduled time has been reached;
 * Upcoming = pending, scheduled for a later time (even later today).
 */
export const getFollowUpTab = (item, now = new Date()) => {
  if (item.deleted_at || isFollowUpCancelled(item)) return 'trash';
  if (item.status === 'completed') return 'completed';
  if (item.status !== 'pending') return null;
  return new Date(item.due_at) <= now ? 'due' : 'upcoming';
};

/** Changes once a day — part of the list query key so Due/Upcoming re-split after midnight. */
export const getDayKey = (now = new Date()) => startOfDay(now).toISOString();

/**
 * GET /api/chats/follow-ups/ filters for each tab (must agree with `getFollowUpTab`):
 * Due = pending, due up to now · Upcoming = pending, due after now · Completed = completed ·
 * Trash = trashed.
 */
export const getTabFilters = (tab, now = new Date()) => {
  switch (tab) {
    case 'due':
      return { status: 'pending', due_date_to: now.toISOString() };
    case 'upcoming':
      return { status: 'pending', due_date_from: new Date(now.getTime() + 1).toISOString() };
    case 'completed':
      return { completed: true };
    case 'trash':
      return { trashed: true };
    default:
      return {};
  }
};

/** Same matching the API's `search` does (title or description), for realtime cache updates. */
export const matchesServerSearch = (item, search) => {
  const query = (search || '').trim().toLowerCase();
  if (!query) return true;
  return `${item.title || ''} ${item.description || ''}`.toLowerCase().includes(query);
};

/** Status pill for the details panel: Due (red when overdue), Upcoming or Completed. */
export const getFollowUpStatusMeta = (item, now = new Date()) => {
  if (item.deleted_at || item.is_deleted) return { label: 'Deleted', className: 'border-red-200 bg-red-50 text-red-600' };
  if (isFollowUpCancelled(item)) return { label: 'Cancelled', className: 'border-red-200 bg-red-50 text-red-600' };
  if (item.status === 'completed') return { label: 'Completed', className: 'border-green-200 bg-green-50 text-green-700' };
  if (getFollowUpTab(item, now) === 'upcoming') return { label: 'Upcoming', className: 'border-gray-200 bg-white text-gray-700' };
  if (isOverdue(item, now)) return { label: 'Overdue', className: 'border-red-200 bg-red-50 text-red-600' };
  return { label: 'Due', className: 'border-blue-200 bg-blue-50 text-blue-600' };
};

const GROUP_LABELS = { overdue: 'Overdue', today: 'Today', tomorrow: 'Tomorrow', later: 'Later', earlier: 'Earlier' };
const GROUP_ORDER = ['overdue', 'today', 'tomorrow', 'later', 'earlier'];

const getGroupKey = (item, tab, now) => {
  const diff = dayDiff(item.due_at, now);
  if (tab !== 'completed' && isOverdue(item, now)) return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff > 1) return 'later';
  return 'earlier';
};

/**
 * Items of one tab, grouped Overdue (red, top) → Today → Tomorrow → Later (→ Earlier for
 * completed). Trash isn't grouped: newest deleted first.
 */
export const groupFollowUps = (items, tab, now = new Date()) => {
  if (tab === 'trash') {
    // Newest first: trashed by trash time, cancelled (not trashed) by their last update
    const removedAt = (item) => new Date(item.deleted_at || item.updated_at || 0);
    const sorted = [...items].sort((a, b) => removedAt(b) - removedAt(a));
    return sorted.length ? [{ key: 'trash', label: null, items: sorted }] : [];
  }

  if (tab === 'due') {
    // Everything here has reached its time — one red "Overdue" section (no Today split), oldest first
    const sorted = [...items].sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    return sorted.length ? [{ key: 'overdue', label: GROUP_LABELS.overdue, isDanger: true, items: sorted }] : [];
  }

  const groups = {};
  items.forEach((item) => {
    const key = getGroupKey(item, tab, now);
    (groups[key] = groups[key] || []).push(item);
  });

  return GROUP_ORDER.filter((key) => groups[key]).map((key) => ({
    key,
    label: GROUP_LABELS[key],
    isDanger: key === 'overdue',
    items: groups[key].sort((a, b) =>
      key === 'earlier' ? new Date(b.due_at) - new Date(a.due_at) : new Date(a.due_at) - new Date(b.due_at)
    ),
  }));
};

const timeLabel = formatTime24;

/** List card: time for today ("13:54"), otherwise a short date ("28 Oct"). */
export const formatListDue = (iso, now = new Date()) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (dayDiff(iso, now) === 0) return timeLabel(date);
  const options = { day: '2-digit', month: 'short' };
  if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
  return date.toLocaleDateString('en-GB', options);
};

/** "Today • 13:54", "Tomorrow • 10:30", "02 Nov 2026 • 11:00" */
export const formatDayAndTime = (iso, now = new Date()) => {
  if (!iso) return '';
  const date = new Date(iso);
  const diff = dayDiff(iso, now);
  const day =
    diff === 0 ? 'Today'
      : diff === 1 ? 'Tomorrow'
        : diff === -1 ? 'Yesterday'
          : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${day} • ${timeLabel(date)}`;
};

export const getConversationName = (conversation) =>
  conversation?.primary_contact?.name?.trim() ||
  conversation?.name ||
  conversation?.primary_contact?.wa_phone ||
  (conversation?.id != null ? `Chat ${conversation.id}` : 'Customer');
